import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, updateDoc, Timestamp, doc } from 'firebase/firestore';
import db from '../firebase';
import { getFirebaseErrorMessage } from '../utils/validation';

function useTodoItems(userId) {
  const [todoItems, setTodoItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(collection(db, "todolist"), where("userId", "==", userId), orderBy("createdEditedTime", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const items = querySnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .sort((a, b) => {
              if (a.done === b.done) {
                return b.createdEditedTime.toMillis() - a.createdEditedTime.toMillis();
              }
              return a.done ? 1 : -1;
            });
          setTodoItems(items);
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error processing todo items:', err);
          setError('Failed to process todo items');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching todo items:', err);
        const errorMessage = getFirebaseErrorMessage(err.code) || 'Failed to load todo items';
        setError(errorMessage);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const addTodoItem = useCallback(async (text) => {
    try {
      await addDoc(collection(db, "todolist"), {
        text,
        done: false,
        createdEditedTime: Timestamp.now(),
        userId
      });
      return { success: true, error: null };
    } catch (err) {
      console.error('Error adding todo item:', err);
      const errorMessage = getFirebaseErrorMessage(err.code) || 'Failed to add todo item';
      return { success: false, error: errorMessage };
    }
  }, [userId]);

  const removeTodoItem = useCallback(async (id) => {
    try {
      await deleteDoc(doc(db, "todolist", id));
      return { success: true, error: null };
    } catch (err) {
      console.error('Error removing todo item:', err);
      const errorMessage = getFirebaseErrorMessage(err.code) || 'Failed to delete todo item';
      return { success: false, error: errorMessage };
    }
  }, []);

  const toggleTodoItem = useCallback(async (id, done) => {
    try {
      const todoListItemRef = doc(db, "todolist", id);
      await updateDoc(todoListItemRef, {
        done,
        createdEditedTime: Timestamp.now(),
      });
      return { success: true, error: null };
    } catch (err) {
      console.error('Error toggling todo item:', err);
      const errorMessage = getFirebaseErrorMessage(err.code) || 'Failed to update todo item';
      return { success: false, error: errorMessage };
    }
  }, []);

  const updateTodoItem = useCallback(async (id, text) => {
    try {
      const todoListItemRef = doc(db, "todolist", id);
      await updateDoc(todoListItemRef, {
        text,
        createdEditedTime: Timestamp.now(),
      });
      return { success: true, error: null };
    } catch (err) {
      console.error('Error updating todo item:', err);
      const errorMessage = getFirebaseErrorMessage(err.code) || 'Failed to update todo item';
      return { success: false, error: errorMessage };
    }
  }, []);

  return {
    todoItems,
    loading,
    error,
    addTodoItem,
    removeTodoItem,
    toggleTodoItem,
    updateTodoItem
  };
}

export default useTodoItems;